import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../stores/authStore";
import type { OrgMember, OrgRole } from "@hsh/shared";
import FundingSection from "../components/FundingSection";

interface GeneratedCredentials {
  email: string;
  password: string;
}

export default function OrganizationPage() {
  const { user } = useAuthStore();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add/Create user form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"admin" | "member">("member");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [generatedCredentials, setGeneratedCredentials] =
    useState<GeneratedCredentials | null>(null);

  // Reset password modal
  const [resetPasswordMember, setResetPasswordMember] =
    useState<OrgMember | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Delete confirmation
  const [deletingMember, setDeletingMember] = useState<OrgMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isOwner = user?.organization?.orgRole === "owner";

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getOrganizationMembers();
      setMembers(response.data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load organization members"
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim()) return;

    setIsCreating(true);
    setCreateError(null);
    setGeneratedCredentials(null);

    try {
      const response = await api.createOrganizationUser(
        newEmail.trim(),
        newName.trim(),
        newRole
      );
      setGeneratedCredentials({
        email: newEmail.trim(),
        password: response.data.generatedPassword,
      });
      setNewEmail("");
      setNewName("");
      setNewRole("member");
      loadMembers();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Failed to create user"
      );
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDeleteUser() {
    if (!deletingMember) return;

    setIsDeleting(true);
    try {
      await api.deleteOrganizationUser(deletingMember.userId);
      setDeletingMember(null);
      loadMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleResetPassword() {
    if (!resetPasswordMember) return;

    setIsResetting(true);
    try {
      const response = await api.resetUserPassword(resetPasswordMember.userId);
      setNewPassword(response.data.newPassword);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to reset password");
      setResetPasswordMember(null);
    } finally {
      setIsResetting(false);
    }
  }

  async function handleRoleChange(member: OrgMember, newRole: OrgRole) {
    try {
      await api.updateMemberRole(member.userId, newRole);
      loadMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update role");
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  if (!user?.organization) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-yellow-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            No Organization
          </h2>
          <p className="text-yellow-700">
            You are not currently a member of any organization. Contact an
            administrator to be added to an organization.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Organization Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 md:w-6 md:h-6 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {user.organization.organizationName}
              </h1>
              <p className="text-xs md:text-sm text-gray-500">
                Your role:{" "}
                <span className="font-medium capitalize">
                  {user.organization.orgRole}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Create User Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-semibold text-gray-900">
              Add New User
            </h2>
            <button
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setGeneratedCredentials(null);
                setCreateError(null);
              }}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              {showCreateForm ? "Cancel" : "+ Create User"}
            </button>
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="john@council.gov.uk"
                    className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role
                </label>
                <select
                  id="role"
                  value={newRole}
                  onChange={(e) =>
                    setNewRole(e.target.value as "admin" | "member")
                  }
                  className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isCreating || !newEmail.trim() || !newName.trim()}
                  className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? "Creating..." : "Create User"}
                </button>
              </div>

              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {createError}
                </div>
              )}
            </form>
          )}

          {/* Generated Credentials Display */}
          {generatedCredentials && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 mb-2">
                    User Created Successfully!
                  </h3>
                  <p className="text-sm text-green-700 mb-3">
                    Share these credentials with the new user. They should
                    change their password after first login.
                  </p>
                  <div className="bg-white rounded-md p-3 border border-green-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Email:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {generatedCredentials.email}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(generatedCredentials.email)
                          }
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Copy email"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Password:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-yellow-100 px-2 py-1 rounded text-yellow-800">
                          {generatedCredentials.password}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(generatedCredentials.password)
                          }
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Copy password"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `Email: ${generatedCredentials.email}\nPassword: ${generatedCredentials.password}`
                      )
                    }
                    className="mt-3 text-sm text-green-700 hover:text-green-800 font-medium"
                  >
                    Copy All Credentials
                  </button>
                </div>
                <button
                  onClick={() => setGeneratedCredentials(null)}
                  className="text-green-500 hover:text-green-700"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Members List */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
            <h2 className="text-base md:text-lg font-semibold text-gray-900">
              Team Members
            </h2>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              {members.length} member{members.length !== 1 ? "s" : ""} in your
              organization
            </p>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              Loading members...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No members found. Create your first team member above.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {members.map((member) => (
                <li
                  key={member.userId}
                  className="px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-base md:text-lg font-medium text-gray-600">
                        {member.userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm md:text-base truncate">
                        {member.userName}
                        {member.userId === user?.userId && (
                          <span className="ml-2 text-xs text-primary-600">
                            (You)
                          </span>
                        )}
                      </p>
                      <p className="text-xs md:text-sm text-gray-500 truncate">
                        {member.userEmail}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:gap-3 ml-12 sm:ml-0">
                    {/* Role selector/badge */}
                    {isOwner && member.orgRole !== "owner" ? (
                      <select
                        value={member.orgRole}
                        onChange={(e) =>
                          handleRoleChange(member, e.target.value as OrgRole)
                        }
                        className="px-2 md:px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span
                        className={`px-2 md:px-3 py-1 text-xs md:text-sm rounded-full ${
                          member.orgRole === "owner"
                            ? "bg-purple-100 text-purple-700"
                            : member.orgRole === "admin"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {member.orgRole.charAt(0).toUpperCase() +
                          member.orgRole.slice(1)}
                      </span>
                    )}

                    {/* Reset Password Button */}
                    {member.orgRole !== "owner" &&
                      member.userId !== user?.userId && (
                        <button
                          onClick={() => setResetPasswordMember(member)}
                          className="p-2 text-gray-400 hover:text-amber-600 transition-colors"
                          title="Reset password"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                            />
                          </svg>
                        </button>
                      )}

                    {/* Delete Button */}
                    {member.orgRole !== "owner" &&
                      member.userId !== user?.userId && (
                        <button
                          onClick={() => setDeletingMember(member)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete user"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Funding Section */}
        <div className="mt-6 md:mt-8">
          <FundingSection />
        </div>

        {/* Delete Confirmation Modal */}
        {deletingMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete User
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete{" "}
                <strong>{deletingMember.userName}</strong> (
                {deletingMember.userEmail})? This action cannot be undone and
                will permanently remove their account.
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={() => setDeletingMember(null)}
                  disabled={isDeleting}
                  className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isDeleting}
                  className="w-full sm:w-auto px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "Delete User"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPasswordMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              {!newPassword ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Reset Password
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Reset the password for{" "}
                    <strong>{resetPasswordMember.userName}</strong> (
                    {resetPasswordMember.userEmail})? A new password will be
                    generated.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setResetPasswordMember(null);
                        setNewPassword(null);
                      }}
                      disabled={isResetting}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={isResetting}
                      className="px-4 py-2 text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {isResetting ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Password Reset
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    New password for{" "}
                    <strong>{resetPasswordMember.userName}</strong>:
                  </p>
                  <div className="bg-gray-100 rounded-md p-3 mb-4 flex items-center justify-between">
                    <code className="text-lg font-mono text-amber-700">
                      {newPassword}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newPassword)}
                      className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                      title="Copy password"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                        />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                    Please share this password securely with the user. They
                    should change it after logging in.
                  </p>
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setResetPasswordMember(null);
                        setNewPassword(null);
                      }}
                      className="px-4 py-2 text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
